import { useState, useEffect, useCallback } from 'react';
import { findBestMovieMatch, extractMovieInfo, getStreamingProviders, tmdbIdOf } from '../services/tmdbService';
import { getBatchedTMDBData } from '../utils/batchTMDBLoader';

// Custom hook for managing TMDB data for individual movies. Pass the movie's
// stored TMDB id whenever it has one: the lookup is then exact, and the
// title is never searched.
export const useTMDBMovie = (title, year, tmdbId = null) => {
  const [tmdbData, setTmdbData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchMovieData = useCallback(async () => {
    if (!title) return;

    setLoading(true);
    setError(null);

    try {
      // Use the batched loader which handles caching and API efficiency
      const movieInfo = await getBatchedTMDBData(title, year, tmdbId);
      setTmdbData(movieInfo);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching TMDB data:', err);
    } finally {
      setLoading(false);
    }
  }, [title, year, tmdbId]);

  useEffect(() => {
    fetchMovieData();
  }, [fetchMovieData]);

  return { tmdbData, loading, error, refetch: fetchMovieData };
};

// Custom hook for managing streaming providers
export const useStreamingProviders = (tmdbId) => {
  const [providers, setProviders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchProviders = useCallback(async () => {
    if (!tmdbId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const providerData = await getStreamingProviders(tmdbId);
      setProviders(providerData);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching streaming providers:', err);
    } finally {
      setLoading(false);
    }
  }, [tmdbId]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  return { providers, loading, error, refetch: fetchProviders };
};

// Custom hook for batch processing movies with TMDB data
export const useTMDBBatch = () => {
  const [enrichedMovies, setEnrichedMovies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  const enrichMovies = useCallback(async (movies) => {
    if (!movies || movies.length === 0) return;
    
    setLoading(true);
    setError(null);
    setProgress(0);
    
    try {
      const enriched = [];
      
      for (let i = 0; i < movies.length; i++) {
        const movie = movies[i];
        
        try {
          const movieDetails = await findBestMovieMatch(movie.title, movie.year, tmdbIdOf(movie));
          const movieInfo = extractMovieInfo(movieDetails);
          
          enriched.push({
            ...movie,
            tmdbData: movieInfo,
            // Merge TMDB data with existing movie data
            runtime: movieInfo?.runtime || movie.runtime,
            plot: movieInfo?.plot,
            posterUrl: movieInfo?.posterUrl,
            trailerUrl: movieInfo?.trailerUrl,
            director: movieInfo?.director,
            writers: movieInfo?.writers,
            stars: movieInfo?.stars,
            originalLanguage: movieInfo?.originalLanguage,
            genres: movieInfo?.genres,
            tmdbRating: movieInfo?.rating,
            tmdbId: movieInfo?.id
          });
          
          // Add small delay to respect API rate limits
          await new Promise(resolve => setTimeout(resolve, 250));
        } catch (movieError) {
          console.error(`Error enriching movie ${movie.title}:`, movieError);
          enriched.push(movie); // Keep original if enrichment fails
        }
        
        setProgress(Math.round(((i + 1) / movies.length) * 100));
      }
      
      setEnrichedMovies(enriched);
    } catch (err) {
      setError(err.message);
      console.error('Error in batch enrichment:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { enrichedMovies, loading, error, progress, enrichMovies };
};
